import React, { useEffect, useState } from 'react';
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader, Col, Row, Spinner, Alert } from 'reactstrap';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
  background: toRgba(ORANGE_COLORS.primary, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const iconGlyph = {
  color: ORANGE_COLORS.primary,
  fontSize: 16,
};

function ViolatedCompliance() {
  const [barData, setBarData] = useState([]);
  const [barLabels, setBarLabels] = useState([]);
  const [donutData, setDonutData] = useState([]);
  const [frameworkLabels, setFrameworkLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const DistributedColumn = ({ seriesData, categories }) => {
    if (!seriesData.length || !categories.length) {
      return (
        <div className="text-center py-5">
          <div style={{ ...iconChip, width: 48, height: 48, margin: "0 auto 12px" }}>
            <i className="ri-bar-chart-line" style={iconGlyph}></i>
          </div>
          <p className="text-muted">No control data available</p>
        </div>
      );
    }

    const series = [{ data: seriesData }];
    
    var options = {
      chart: {
        height: 350,
        type: 'bar',
        toolbar: {
          show: false
        }
      },
      colors: [ORANGE_COLORS.primary, ORANGE_COLORS.dark, ORANGE_COLORS.darker, ORANGE_COLORS.light, ORANGE_COLORS.lighter, ORANGE_COLORS.muted],
      plotOptions: {
        bar: {
          columnWidth: '45%',
          distributed: true,
        }
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: [ORANGE_COLORS.primary, ORANGE_COLORS.dark, ORANGE_COLORS.darker, ORANGE_COLORS.light, ORANGE_COLORS.lighter, ORANGE_COLORS.muted],
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Violations Count',
          style: {
            color: ORANGE_COLORS.dark
          }
        }
      }
    };

    return (
      <ReactApexChart 
        dir="ltr" 
        className="apex-charts" 
        series={series} 
        options={options} 
        type="bar" 
        height={350} 
      />
    );
  };

  const UpdateDonut = ({ seriesData, labels }) => {
    if (!seriesData.some(val => val > 0)) {
      return (
        <div className="text-center py-5">
          <div style={{ ...iconChip, width: 48, height: 48, margin: "0 auto 12px" }}>
            <i className="ri-pie-chart-line" style={iconGlyph}></i>
          </div>
          <p className="text-muted">No framework data available</p>
        </div>
      );
    }

    var options = {
      chart: {
        height: 350,
        type: 'donut',
      },
      labels: labels,
      dataLabels: {
        enabled: true
      },
      legend: {
        position: 'bottom'
      },
      colors: [ORANGE_COLORS.primary, ORANGE_COLORS.dark, ORANGE_COLORS.light, ORANGE_COLORS.darker, ORANGE_COLORS.lighter]
    };

    return (
      <ReactApexChart 
        dir="ltr"
        className="apex-charts"
        series={seriesData}
        options={options}
        type="donut"
        height={350}
      />
    );
  };

  useEffect(() => {
    const fetchMappings = async () => {
      setLoading(true);
      setError(null);
      try {
        const authUser = JSON.parse(sessionStorage.getItem("authUser"));
        const headers = {
          "Content-Type": "application/json",
          ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
        };
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const result = await fetch(
          `${API_BASE_URL}/threats/compliance-mappings?limit=500`, 
          { headers, signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        
        if (!result.ok) {
          throw new Error(`HTTP error! status: ${result.status}`);
        }
        
        const data = await result.json();
        console.log('Violated Compliance Data:', data);

        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const frameworkCounts = {};

          data.data.forEach(({ framework }) => {
            frameworkCounts[framework] = (frameworkCounts[framework] || 0) + 1;
          });

          setBarData(Object.values(frameworkCounts));
          setBarLabels(Object.keys(frameworkCounts));
          setDonutData(Object.values(frameworkCounts));
          setFrameworkLabels(Object.keys(frameworkCounts));
        } else {
          setError('No compliance violations found');
        }
      } catch (err) {
        console.error("Failed to fetch compliance mappings:", err);
        if (err.name === 'AbortError') {
          setError('Request timed out');
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMappings();
  }, []);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
            <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <Spinner style={{ color: ORANGE_COLORS.primary }} />
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
            <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <Spinner style={{ color: ORANGE_COLORS.primary }} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
  <Row className="g-3 mb-4">
    <Col lg={6}>
      <Card style={cardShell}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-bar-chart-line" style={iconGlyph}></i>
            </span>
            Top Violated Controls
          </h5>
        </CardHeader>
        <CardBody>
          {error ? (
            <Alert style={{ backgroundColor: ORANGE_COLORS.subtle, color: ORANGE_COLORS.darker, border: `1px solid ${ORANGE_COLORS.muted}` }}>
              <i className="ri-alert-line me-2"></i>
              {error}
            </Alert>
          ) : (
            <DistributedColumn 
              seriesData={barData} 
              categories={barLabels} 
            />
          )}
        </CardBody>
      </Card>
    </Col>

    <Col lg={6}>
      <Card style={cardShell}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-pie-chart-line" style={iconGlyph}></i>
            </span>
            Compliance Status Breakdown
          </h5>
        </CardHeader>
        <CardBody>
          {error ? (
            <Alert style={{ backgroundColor: ORANGE_COLORS.subtle, color: ORANGE_COLORS.darker, border: `1px solid ${ORANGE_COLORS.muted}` }}>
              <i className="ri-alert-line me-2"></i>
              {error}
            </Alert>
          ) : (
            <UpdateDonut 
              seriesData={donutData} 
              labels={frameworkLabels} 
            />
          )}
        </CardBody>
      </Card>
    </Col>
  </Row>
);
}

export default ViolatedCompliance;
